import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Calculator, TrendingUp, DollarSign, Percent, Info, Sparkles, ArrowRight, CheckCircle2, AlertTriangle } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Seo } from "../components/Seo";

const CTR_OPTIONS = [
  { id: "p1", label: "#1", ctr: 0.3 },
  { id: "p2", label: "#2", ctr: 0.15 },
  { id: "p3", label: "#3", ctr: 0.1 },
  { id: "top5", label: "Top 5 (avg)", ctr: 0.06 },
  { id: "top10", label: "Top 10 (avg)", ctr: 0.02 },
];

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

const money = (v) =>
  v.toLocaleString("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 });

const InfoTip = ({ text }) => (
  <span className="relative group inline-flex align-middle ml-1.5">
    <Info className="h-3.5 w-3.5 text-slate-400 cursor-help" />
    <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-60 rounded-lg bg-slate-900 px-3 py-2 text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity z-20 shadow-xl">
      {text}
    </span>
  </span>
);

const Field = ({ label, tip, children }) => (
  <div>
    <label className="text-sm font-medium text-slate-700 flex items-center">
      {label}
      {tip && <InfoTip text={tip} />}
    </label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const InputsCard = ({ form, onChange }) => (
  <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-7" data-testid="roi-inputs-card">
    <h2 className="font-display text-xl font-bold text-slate-900 flex items-center gap-2">
      <Calculator className="h-5 w-5 text-indigo-600" /> Your numbers
    </h2>
    <div className="mt-6 grid gap-5 sm:grid-cols-2">
      <Field label="Current monthly traffic">
        <Input type="number" min="0" value={form.traffic} onChange={onChange("traffic")} placeholder="e.g. 2000" className="rounded-lg" data-testid="roi-input-traffic" />
      </Field>
      <Field label="Conversion rate (%)" tip="Percentage of visitors who become customers. E-commerce averages 1–3%; lead-gen sites 2–5%.">
        <Input type="number" min="0" step="0.1" value={form.convRate} onChange={onChange("convRate")} placeholder="e.g. 2" className="rounded-lg" data-testid="roi-input-conv" />
      </Field>
      <Field label="Avg order value / LTV ($)">
        <Input type="number" min="0" value={form.aov} onChange={onChange("aov")} placeholder="e.g. 150" className="rounded-lg" data-testid="roi-input-aov" />
      </Field>
      <Field label="Target keyword search volume" tip="Combined monthly searches for the keywords you want to rank for.">
        <Input type="number" min="0" value={form.volume} onChange={onChange("volume")} placeholder="e.g. 10000" className="rounded-lg" data-testid="roi-input-volume" />
      </Field>
      <Field label="Projected ranking position" tip="Benchmark organic CTR: #1 ≈ 30%, #2 ≈ 15%, #3 ≈ 10%, Top 5 ≈ 6%, Top 10 ≈ 2% of searches click through.">
        <select
          value={form.position}
          onChange={onChange("position")}
          data-testid="roi-input-position"
          className="w-full h-10 rounded-lg border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        >
          {CTR_OPTIONS.map((o) => (
            <option key={o.id} value={o.id}>{o.label} — {(o.ctr * 100).toFixed(0)}% CTR</option>
          ))}
        </select>
      </Field>
      <Field label="Monthly campaign cost ($)">
        <Input type="number" min="0" value={form.cost} onChange={onChange("cost")} placeholder="e.g. 1500" className="rounded-lg" data-testid="roi-input-cost" />
      </Field>
    </div>
  </div>
);

const MetricCard = ({ icon: Icon, label, value, tone, testId }) => {
  const tones = {
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-100",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    amber: "bg-amber-50 text-amber-700 border-amber-100",
    rose: "bg-rose-50 text-rose-700 border-rose-100",
  };
  return (
    <div className={`rounded-2xl border p-6 ${tones[tone]}`} data-testid={testId}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide opacity-80">
        <Icon className="h-4 w-4" /> {label}
      </div>
      <p className="mt-2 font-display text-3xl font-extrabold">{value}</p>
    </div>
  );
};

const Bar = ({ label, value, max, color, testId }) => (
  <div data-testid={testId}>
    <div className="flex justify-between text-sm mb-1.5">
      <span className="font-medium text-slate-700">{label}</span>
      <span className="font-semibold text-slate-900">{money(value)}</span>
    </div>
    <div className="h-4 rounded-full bg-slate-100 overflow-hidden">
      <div
        className={`h-full rounded-full ${color} transition-all duration-700`}
        style={{ width: `${max > 0 ? Math.max((value / max) * 100, value > 0 ? 2 : 0) : 0}%` }}
      />
    </div>
  </div>
);

const ChartsCard = ({ r }) => {
  const maxRev = Math.max(r.currentRevenue, r.projectedRevenue, 1);
  const maxProfit = Math.max(r.cost, Math.abs(r.netProfit), 1);
  return (
    <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-7 space-y-6" data-testid="roi-charts-card">
      <h3 className="font-display text-lg font-bold text-slate-900">Revenue & profit comparison</h3>
      <div className="space-y-4">
        <Bar label="Current monthly revenue" value={r.currentRevenue} max={maxRev} color="bg-slate-400" testId="bar-current-revenue" />
        <Bar label="Projected monthly revenue" value={r.projectedRevenue} max={maxRev} color="bg-indigo-500" testId="bar-projected-revenue" />
      </div>
      <div className="border-t border-slate-100 pt-5 space-y-4">
        <Bar label="Monthly campaign cost" value={r.cost} max={maxProfit} color="bg-amber-400" testId="bar-cost" />
        <Bar label="Monthly net profit" value={Math.max(r.netProfit, 0)} max={maxProfit} color="bg-emerald-500" testId="bar-net-profit" />
        {r.netProfit < 0 && (
          <p className="text-xs text-rose-600">Net profit is negative ({money(r.netProfit)}) at these assumptions.</p>
        )}
      </div>
    </div>
  );
};

const ResultsDashboard = ({ r }) => (
  <div className="space-y-5">
    <div className="grid gap-4 sm:grid-cols-3">
      <MetricCard icon={TrendingUp} label="Projected traffic gain" value={`+${Math.round(r.newTraffic).toLocaleString()} /mo`} tone="indigo" testId="metric-traffic-gain" />
      <MetricCard icon={DollarSign} label="Estimated new revenue" value={money(r.newRevenue)} tone="emerald" testId="metric-new-revenue" />
      <MetricCard
        icon={Percent}
        label="Estimated ROI"
        value={r.roi === null ? "—" : `${Math.round(r.roi)}%`}
        tone={r.roi === null ? "amber" : r.roi > 0 ? "emerald" : "rose"}
        testId="metric-roi"
      />
    </div>

    {r.roi !== null && r.roi > 0 && (
      <div className="flex items-start gap-3 rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800" data-testid="breakeven-banner">
        <CheckCircle2 className="h-5 w-5 shrink-0" />
        <p>
          <strong>Positive ROI.</strong> You only need{" "}
          <strong>{r.breakEvenConversions.toLocaleString()} conversion{r.breakEvenConversions === 1 ? "" : "s"}</strong>{" "}
          per month to cover the {money(r.cost)} campaign cost — this projection delivers ≈{Math.round(r.projectedConversions).toLocaleString()}.
        </p>
      </div>
    )}
    {r.roi !== null && r.roi <= 0 && (
      <div className="flex items-start gap-3 rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800" data-testid="negative-roi-banner">
        <AlertTriangle className="h-5 w-5 shrink-0" />
        <p>At these assumptions the campaign doesn't break even yet — try a higher ranking position, better conversion rate or larger keyword volume.</p>
      </div>
    )}

    <ChartsCard r={r} />
  </div>
);

const SeoRoiCalculator = () => {
  const [form, setForm] = useState({ traffic: "2000", convRate: "2", aov: "150", volume: "10000", position: "p3", cost: "1500" });
  const onChange = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const r = useMemo(() => {
    const ctr = (CTR_OPTIONS.find((o) => o.id === form.position) || CTR_OPTIONS[2]).ctr;
    const traffic = num(form.traffic);
    const conv = num(form.convRate) / 100;
    const aov = num(form.aov);
    const volume = num(form.volume);
    const cost = num(form.cost);
    const newTraffic = volume * ctr;
    const newRevenue = newTraffic * conv * aov;
    const currentRevenue = traffic * conv * aov;
    const netProfit = newRevenue - cost;
    return {
      cost, newTraffic, newRevenue, currentRevenue, netProfit,
      projectedRevenue: currentRevenue + newRevenue,
      roi: cost > 0 ? (netProfit / cost) * 100 : null,
      breakEvenConversions: aov > 0 ? Math.ceil(cost / aov) : 0,
      projectedConversions: newTraffic * conv,
    };
  }, [form]);

  return (
    <div>
      <Seo
        title="SEO ROI & Traffic Calculator — Estimate Your Returns | OzzieeTech"
        description="Free SEO ROI calculator: estimate traffic gains, new revenue and return on investment from ranking higher on Google. Based on real organic CTR benchmarks."
        path="/seo/roi-calculator"
      />
      <section className="relative pt-32 pb-12 bg-gradient-to-b from-slate-50 to-white overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-60" />
        <div className="container-wide relative">
          <Link to="/seo" className="text-sm font-medium text-slate-500 hover:text-primary">← Back to SEO Services</Link>
          <span className="mt-5 flex w-fit items-center gap-2 rounded-full bg-indigo-100 px-4 py-1.5 text-xs font-semibold text-indigo-700">
            <Calculator className="h-3.5 w-3.5" /> Free SEO Tool
          </span>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 text-balance max-w-3xl">
            SEO ROI &amp; Traffic Calculator
          </h1>
          <p className="mt-4 text-lg text-slate-500 max-w-2xl leading-relaxed">
            Estimate how much traffic, revenue and return on investment ranking higher on Google could
            generate for your business — using real-world organic click-through benchmarks.
          </p>
        </div>
      </section>

      <section className="pb-16">
        <div className="container-wide grid gap-8 lg:grid-cols-2 items-start">
          <InputsCard form={form} onChange={onChange} />
          <ResultsDashboard r={r} />
        </div>
      </section>

      <section className="pb-20">
        <div className="container-wide">
          <div className="rounded-2xl bg-slate-900 text-white p-8 flex flex-col sm:flex-row items-center justify-between gap-5">
            <div>
              <h2 className="font-display text-xl font-extrabold flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" /> Like these numbers?</h2>
              <p className="mt-1 text-slate-300 text-sm">Run a free AI audit to see exactly what's holding your rankings back — then let us get you there.</p>
            </div>
            <div className="flex gap-3 shrink-0">
              <Button asChild className="rounded-lg bg-primary hover:bg-primary/90">
                <Link to="/seo/auto-engine">Free AI SEO Audit <ArrowRight className="h-4 w-4 ml-1.5" /></Link>
              </Button>
              <Button asChild variant="outline" className="rounded-lg border-white/30 bg-transparent text-white hover:bg-white/10">
                <Link to="/seo#contact">Talk to us</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SeoRoiCalculator;
